import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Divider,
} from "@mui/material";
import { Vendor, VendorType } from "../types";
import { useAppDispatch, useAppSelector } from "../hooks";
import { createVendor } from "../api";
import NotificationService from "../components/notification/NotificationService";
import { useVendorForm } from "../hooks/useVendorForm";
import { setVendors } from "../features/appSlice";
import { getGuid } from "../utils";

interface VendorDialogProps {
  open: boolean;
  onClose: () => void;
  vendor: Vendor;
  isNew: boolean;
  vendorTypes?: VendorType[];
  handleChange: (field: keyof Vendor, value: any) => void;
  zip?: string;
  handleZipChange?: (zip: string) => void;
  setSelectedVendor?: (vendor: Vendor) => void;
}

const VendorDialog: React.FC<VendorDialogProps> = ({
  open,
  onClose,
  vendor,
  vendorTypes = [],
  handleChange,
}) => {
  const vendors = useAppSelector((state) => state.app.vendors);
  const contacts = useAppSelector((state) => state.app.contacts);
  const dispatch = useAppDispatch();
  const save = () => {
    createVendor({
      ...vendor,
      type: vendor.type?.id,
      contact: vendor.contact.id,
      visible: true,
      mainVendor: undefined,
    })
      .then((v) => {
        NotificationService.showToast({
          severity: "success",
          text: "Leverandør blev succeesfuldt oprettet",
        });
        dispatch(setVendors([...vendors, { ...vendor, id: v.id }]));
        onClose();
      })
      .catch((e) => {
        NotificationService.showToast({
          severity: "error",
          text: "Der skete en fejl. Kunne ikke oprette leverandør",
        });
      });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent>
        {/* Contact Selection */}
        <Typography variant="h6" gutterBottom>
          Kontaktperson
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl fullWidth margin="dense">
            <InputLabel>Vælg Kontakt</InputLabel>
            <Select
              value={vendor.contact?.id || ""}
              onChange={(e) => {
                const selected = contacts.find((c) => c.id === e.target.value);
                if (selected) handleChange("contact", selected);
              }}
            >
              {contacts.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name} ({c.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Vendor Info */}
        <Typography variant="h6" gutterBottom>
          Hjemmeside Info
        </Typography>
        {!vendor?.mainVendor && (
          <TextField
            label="Virksomhedsnavn"
            fullWidth
            margin="dense"
            value={vendor.name || ""}
            onChange={(e) => handleChange("name", e.target.value)}
          />
        )}

        <FormControl margin="dense" fullWidth>
          <InputLabel>Kategori</InputLabel>
          <Select
            value={vendor.type?.id || ""}
            onChange={(e) => {
              const typeObj = vendorTypes.find((t) => t.id === e.target.value);
              handleChange("type", typeObj || null);
            }}
          >
            {vendorTypes.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Box>
          <Button onClick={onClose} color="secondary">
            Annuller
          </Button>
          <Button
            onClick={save}
            variant="contained"
            color="primary"
            disabled={!(vendor?.name && vendor?.type && vendor.contact?.id)}
          >
            Gem
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default VendorDialog;
